import { Alert, Drawer, Skeleton, Space, Table, Tag, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { get_client_ai_policy_preview } from "../../Services/client.service";

const STATUS_CONFIG = {
    ok: { className: "clients-ai-status--ok", labelKey: "detail.ai.status.ok" },
    partially_blocked: { className: "clients-ai-status--partial", labelKey: "detail.ai.status.partiallyBlocked" },
    blocked: { className: "clients-ai-status--blocked", labelKey: "detail.ai.status.blocked" },
};

function formatProvider(provider) {
    if (!provider) return "-";
    return provider.charAt(0).toUpperCase() + provider.slice(1);
}

function metadataTags(metadata = {}) {
    const tags = [];
    const familyTier = [metadata.model_family, metadata.model_tier].filter(Boolean).join(" / ");
    if (familyTier) tags.push(familyTier);
    if (metadata.thinking_enabled !== undefined) {
        tags.push(metadata.thinking_enabled ? "thinking on" : "thinking off");
    }
    if (metadata.max_context_tokens) {
        tags.push(`context ${metadata.max_context_tokens}`);
    }
    if (metadata.max_output_tokens) {
        tags.push(`max output ${metadata.max_output_tokens}`);
    }
    if (metadata.embedding_dimensions) {
        tags.push(`dim ${metadata.embedding_dimensions}`);
    }
    if (metadata.embedding_version) {
        tags.push(`version ${metadata.embedding_version}`);
    }
    if (metadata.contract_content === false) {
        tags.push("no contract content");
    }
    if (metadata.usage_role) {
        tags.push(metadata.usage_role);
    }
    return tags;
}

function formatBlockReason(reason, policy, t) {
    if (!reason) return "-";
    if (policy === "CANADA_ONLY" && reason.includes("no approved Canada-only Bedrock model is configured")) {
        return t("detail.ai.reasons.canadaOnlyUnconfigured");
    }
    if (policy === "CANADA_ONLY" && reason.includes("not marked as a Canada-only Bedrock profile")) {
        return t("detail.ai.reasons.canadaOnlyInvalidScope");
    }
    if (reason.includes("Semantic vector embeddings are disabled by configuration")) {
        return t("detail.ai.reasons.semanticEmbeddingsDisabled");
    }
    if (reason.includes("CANADA_ONLY requires a Canadian Bedrock runtime region for semantic embeddings")) {
        return t("detail.ai.reasons.semanticEmbeddingsCanadaRegion");
    }
    return reason;
}

export default function ClientAiPolicyPreview({ open, onClose, client, isSuperAdmin }) {
    const { t } = useTranslation("clients");
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!open || !client?.id || !isSuperAdmin) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(false);
        setPreview(null);

        get_client_ai_policy_preview(client.id)
            .then((response) => {
                if (cancelled) return;
                if (response?.success && response?.data) {
                    setPreview(response.data);
                } else {
                    setPreview(null);
                    setError(true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPreview(null);
                    setError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, client?.id, isSuperAdmin]);

    const summary = preview?.summary || {};
    const status = STATUS_CONFIG[summary.status] || STATUS_CONFIG.blocked;
    const policyLabel =
        preview?.policy === "STANDARD_BEDROCK_CONTROLLED"
            ? t("form.aiPolicyStandard")
            : preview?.policy === "CANADA_ONLY"
                ? t("form.aiPolicyCanadaOnly")
                : preview?.policy;
    const hasBlockedFeatures = (preview?.features || []).some((feature) => !feature.allowed);
    const showCanadaOnlyWarning = preview?.policy === "CANADA_ONLY" && hasBlockedFeatures;
    const infrastructureServices = preview?.infrastructure_services || [];

    const columns = useMemo(
        () => [
            {
                title: t("detail.ai.columns.feature"),
                dataIndex: "label",
                width: 190,
                render: (label, record) => (
                    <div className="clients-ai-feature-cell">
                        <strong>{label || record.feature}</strong>
                        <span>{record.feature}</span>
                    </div>
                ),
            },
            {
                title: t("detail.ai.columns.status"),
                dataIndex: "allowed",
                width: 120,
                render: (allowed, record) => (
                    <Tag className={
                        record.enabled === false
                            ? "clients-ai-status clients-ai-status--disabled"
                            : allowed
                                ? "clients-ai-status clients-ai-status--ok"
                                : "clients-ai-status clients-ai-status--blocked"
                    }>
                        {record.enabled === false ? t("detail.ai.disabled") : allowed ? t("detail.ai.allowed") : t("detail.ai.blocked")}
                    </Tag>
                ),
            },
            {
                title: t("detail.ai.columns.provider"),
                dataIndex: "provider",
                width: 110,
                render: (provider) => formatProvider(provider),
            },
            {
                title: t("detail.ai.columns.model"),
                dataIndex: "model_id",
                width: 230,
                render: (modelId) => (
                    modelId ? (
                        <Tooltip title={modelId}>
                            <code className="clients-ai-model-id">{modelId}</code>
                        </Tooltip>
                    ) : "-"
                ),
            },
            {
                title: t("detail.ai.columns.scope"),
                dataIndex: "region_scope",
                width: 170,
                render: (scope) => scope ? <Tag className="clients-ai-scope">{scope}</Tag> : "-",
            },
            {
                title: t("detail.ai.columns.metadata"),
                dataIndex: "metadata",
                width: 260,
                render: (metadata) => {
                    const tags = metadataTags(metadata);
                    if (!tags.length) return "-";
                    return (
                        <Space size={[4, 4]} wrap>
                            {tags.map((tag) => (
                                <Tag key={tag} className="clients-ai-meta-tag">{tag}</Tag>
                            ))}
                        </Space>
                    );
                },
            },
            {
                title: t("detail.ai.columns.reason"),
                dataIndex: "reason",
                render: (reason) => formatBlockReason(reason, preview?.policy, t),
            },
        ],
        [preview?.policy, t]
    );
    const infrastructureColumns = useMemo(
        () => [
            { ...columns[0], title: t("detail.ai.columns.service") },
            ...columns.slice(1),
        ],
        [columns, t]
    );

    return (
        <Drawer
            title={t("detail.title")}
            open={open}
            onClose={onClose}
            width={1080}
            destroyOnClose
            className="client-detail-drawer"
        >
            {client && (
                <div className="client-detail-shell">
                    <section className="client-detail-section">
                        <div className="client-detail-heading">
                            <h3>{client.name}</h3>
                            <Tag className={client.is_active ? "clients-status clients-status--active" : "clients-status clients-status--inactive"}>
                                {client.is_active ? t("status.active") : t("status.inactive")}
                            </Tag>
                        </div>
                        <dl className="client-detail-grid">
                            <div>
                                <dt>{t("table.email")}</dt>
                                <dd>{client.contact_email || "-"}</dd>
                            </div>
                            <div>
                                <dt>{t("table.industry")}</dt>
                                <dd>{client.industry || "-"}</dd>
                            </div>
                            <div>
                                <dt>{t("table.workspacePlan")}</dt>
                                <dd className="client-detail-workspace-plan">
                                    <span>{t("table.workspacePlanManaged")}</span>
                                    {client.subscription_plan && (
                                        <small>{t("detail.legacyPlanFallback")}</small>
                                    )}
                                </dd>
                            </div>
                        </dl>
                    </section>

                    {isSuperAdmin && (
                        <section className="client-detail-section client-detail-section--ai">
                            <div className="clients-ai-preview-header">
                                <div>
                                    <p className="clients-ai-eyebrow">{t("detail.ai.eyebrow")}</p>
                                    <h3>{t("detail.ai.title")}</h3>
                                    <p>{t("detail.ai.help")}</p>
                                </div>
                                {preview && (
                                    <Space size={[8, 8]} wrap className="clients-ai-summary">
                                        <Tag className="clients-ai-policy-tag">
                                            {policyLabel}
                                        </Tag>
                                        <Tag className={`clients-ai-status ${status.className}`}>
                                            {t(status.labelKey)}
                                        </Tag>
                                        <span>
                                            {summary.allowed_count || 0} {t("detail.ai.allowedShort")} | {summary.blocked_count || 0} {t("detail.ai.blockedShort")}
                                        </span>
                                    </Space>
                                )}
                            </div>

                            {loading && <Skeleton active paragraph={{ rows: 6 }} />}

                            {!loading && error && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={t("detail.ai.unavailable")}
                                />
                            )}

                            {!loading && !error && preview && showCanadaOnlyWarning && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    className="clients-ai-warning"
                                    message={t("detail.ai.canadaBlockedWarning")}
                                />
                            )}

                            {!loading && !error && preview && (
                                <>
                                    <Table
                                        columns={columns}
                                        dataSource={preview.features || []}
                                        rowKey="feature"
                                        size="small"
                                        pagination={false}
                                        tableLayout="fixed"
                                        scroll={{ x: 1180 }}
                                    />

                                    {infrastructureServices.length > 0 && (
                                        <div className="clients-ai-infra-section">
                                            <div className="clients-ai-infra-heading">
                                                <h4>{t("detail.ai.infrastructureTitle")}</h4>
                                                <p>{t("detail.ai.infrastructureHelp")}</p>
                                            </div>
                                            <Table
                                                columns={infrastructureColumns}
                                                dataSource={infrastructureServices.map((service) => ({
                                                    ...service,
                                                    feature: service.service,
                                                }))}
                                                rowKey="service"
                                                size="small"
                                                pagination={false}
                                                tableLayout="fixed"
                                                scroll={{ x: 1180 }}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </section>
                    )}
                </div>
            )}
        </Drawer>
    );
}
